import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useApp, API, WHATSAPP_URL, websiteChannelHeaders } from "../store";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { HERO_VIDEO_SRC } from "../homeData";
import { Sparkles, RefreshCw, Calendar, MessageCircle, Send, Bot, Check, Award, Users, Zap, Clock } from "lucide-react";

const icon = new L.Icon({
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41],
});

const LIFESTYLE = [
  "https://staging.sharkxelectric.com/api/uploads/bbe160424d8145daa9e71f4d59f8660a.webp",
  "https://images.unsplash.com/photo-1667478828745-0b3ec0c3f97a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1763075958748-9a0c9c8799c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

const PERKS_COPY = {
  en: {
    overline: "Why a SharkX lesson",
    title: "First-time riders fly within 45 minutes.",
    body: "Our certified instructors have taught thousands of riders worldwide. We start you on the safest setup, watch every move, and tweak your technique in real time so you progress fast — and walk away grinning.",
    p1t: "45-min average",
    p1d: "Most riders are flying on foil within their first session — no prior surf experience needed.",
    p2t: "Certified instructors",
    p2d: "Trained on every SharkX board configuration. Bilingual EN / PT / ES.",
    p3t: "All gear included",
    p3d: "We bring the boards, batteries, helmets, vests and remotes. Just show up.",
    p4t: "Refundable",
    p4d: "100% of your demo price is refunded on any SharkX board purchase within 30 days.",
  },
  pt: {
    overline: "Por que uma aula SharkX",
    title: "Iniciantes voam em 45 minutos.",
    body: "Nossos instrutores certificados já ensinaram milhares de riders pelo mundo. Começamos pelo setup mais seguro, observamos cada movimento e ajustamos sua técnica em tempo real — você evolui rápido e sai com um sorriso enorme.",
    p1t: "45 min em média",
    p1d: "A maioria dos alunos já está voando no foil na primeira sessão — sem experiência prévia.",
    p2t: "Instrutores certificados",
    p2d: "Treinados em todas as configurações de pranchas SharkX. Bilíngues PT / EN / ES.",
    p3t: "Tudo incluso",
    p3d: "A gente leva pranchas, baterias, capacetes, coletes e controles. Você só aparece.",
    p4t: "Reembolsável",
    p4d: "100% do valor da demo é reembolsado na compra de qualquer prancha SharkX em até 30 dias.",
  },
  es: {
    overline: "Por qué una clase SharkX",
    title: "Riders nuevos vuelan en 45 minutos.",
    body: "Nuestros instructores certificados han enseñado a miles de riders en el mundo. Empezamos con el setup más seguro, miramos cada movimiento y ajustamos tu técnica en tiempo real.",
    p1t: "45 min promedio",
    p1d: "La mayoría vuela en foil en la primera sesión — sin experiencia previa.",
    p2t: "Instructores certificados",
    p2d: "Entrenados en todas las configuraciones SharkX. Bilingües EN / PT / ES.",
    p3t: "Todo incluido",
    p3d: "Llevamos las tablas, baterías, cascos, chalecos y controles. Solo te presentás.",
    p4t: "Reembolsable",
    p4d: "100% del valor de la demo se reembolsa en la compra de una tabla SharkX en 30 días.",
  },
};

export default function BookDemo() {
  const { t, locale, country, currency } = useApp();
  const [locs, setLocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const mapRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/locations`).then((r) => {
      setLocs(r.data);
      if (r.data.length > 0) setSelected(r.data[0]);
    });
    axios.get(`${API}/lessons/sessions${country ? `?country=${country}` : ""}`).then((r) => setSessions(r.data));
    setMapKey((k) => k + 1);
    return () => {
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (e) {}
        mapRef.current = null;
      }
    };
  }, [country]);

  const fmtSession = (s) => {
    if (currency === "BRL") return `R$ ${Number(s.price_brl).toLocaleString("pt-BR")}`;
    return `$${Number(s.price_usd).toLocaleString("en-US")}`;
  };

  const perks = PERKS_COPY[locale] || PERKS_COPY.en;

  return (
    <main className="bg-white" data-testid="book-page">
      {/* Hero */}
      <section className="relative h-[80vh] overflow-hidden">
        <div className="absolute inset-0">
          <div className="hero-video-wrap">
            <iframe src={HERO_VIDEO_SRC} title={t.book.iframeTitle} frameBorder="0" allow="autoplay; encrypted-media" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-slate-900/40" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16 text-white">
          <div className="overline mb-4 text-white/80">{t.book.heroOverline}</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl">{t.book.heroTitle}</h1>
          <p className="mt-5 max-w-2xl text-base md:text-xl text-white/90 font-light">{t.book.heroBody}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setChatOpen(true)} className="btn-primary" data-testid="open-ai-agent">
              <Bot size={16} /> {t.book.checkAvail}
            </button>
            <a href="#sessions" className="btn-ghost !text-white !border-white" data-testid="cta-sessions">{t.book.pricing}</a>
          </div>
        </div>
      </section>

      {/* Try it first + Rent */}
      <section className="py-14 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="try-rent-section">
        <div className={`grid grid-cols-1 ${country === "BR" ? "md:grid-cols-2" : ""} gap-6`}>
          <div className="card-clean p-7 md:p-10 bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200" data-testid="try-first-card">
            <div className="w-12 h-12 rounded-full bg-sky-500 text-white grid place-items-center mb-4"><RefreshCw size={20} /></div>
            <div className="font-serif-display text-2xl md:text-4xl text-slate-900">{t.book.tryFirst}</div>
            <p className="text-slate-700 mt-3 leading-relaxed text-sm md:text-base">{t.book.tryFirstBody}</p>
          </div>
          {country === "BR" && (
            <div className="card-clean p-7 md:p-10 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200" data-testid="rent-day-card">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white grid place-items-center mb-4"><Calendar size={20} /></div>
              <div className="font-serif-display text-2xl md:text-4xl text-slate-900">{t.book.rentTitle}</div>
              <p className="text-slate-700 mt-3 leading-relaxed text-sm md:text-base">{t.book.rentBody}</p>
              <button onClick={() => setChatOpen(true)} className="btn-primary mt-5" data-testid="rent-day-cta">
                {t.book.rentCta} →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Why a lesson — perks 4-up */}
      <section className="py-16 md:py-20 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="perks-section">
        <div className="text-center mb-10 md:mb-14">
          <div className="overline mb-3">{perks.overline}</div>
          <h2 className="font-serif-display text-3xl md:text-5xl text-slate-900 max-w-3xl mx-auto leading-tight">{perks.title}</h2>
          <p className="text-slate-600 mt-5 max-w-2xl mx-auto text-sm md:text-base">{perks.body}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <Perk icon={<Clock size={22} />} t={perks.p1t} d={perks.p1d} />
          <Perk icon={<Award size={22} />} t={perks.p2t} d={perks.p2d} />
          <Perk icon={<Zap size={22} />} t={perks.p3t} d={perks.p3d} />
          <Perk icon={<Users size={22} />} t={perks.p4t} d={perks.p4d} />
        </div>
      </section>

      {/* Lifestyle teaching split */}
      <section className="py-16 px-6 md:px-12 bg-slate-50">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <img src={LIFESTYLE[0]} alt="Teaching" className="w-full aspect-[5/4] object-cover rounded-md shadow-xl" />
          <div>
            <div className="overline mb-3">{t.book.howItWorks}</div>
            <h3 className="font-serif-display text-3xl md:text-5xl text-slate-900 leading-tight">
              {t.book.howItWorksTitle}
            </h3>
            <ol className="mt-6 space-y-4 text-slate-700">
              {[
                t.book.howItWorksStep1,
                t.book.howItWorksStep2,
                t.book.howItWorksStep3,
                t.book.howItWorksStep4,
              ].map((step, i) => (
                <li key={i} className="flex gap-3"><span className="font-serif-display text-2xl text-sky-500 leading-none">{i + 1}.</span><span className="text-sm md:text-base">{step}</span></li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Map locations + bookings — moved BELOW the lessons content */}
      <section id="sessions" className="py-16 px-6 md:px-12 max-w-[1440px] mx-auto scroll-mt-20" data-testid="locations-section">
        <h2 className="font-serif-display text-3xl md:text-5xl text-slate-900 mb-2">{t.book.title}</h2>
        <p className="text-slate-600 max-w-xl mb-8 text-sm md:text-base">{t.book.chooseLocation}</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-[420px] md:h-[480px] border border-slate-200 rounded-md overflow-hidden" data-testid="map-container">
            <MapContainer
              key={mapKey}
              center={[20, -30]}
              zoom={2}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
              whenCreated={(m) => { mapRef.current = m; }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
              {locs.map((l) => (
                <Marker key={l.id} position={[l.lat, l.lng]} icon={icon} eventHandlers={{ click: () => setSelected(l) }}>
                  <Popup>{l.name}<br />{l.city}, {l.country}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="locations-list">
            {locs.map((l) => (
              <button key={l.id} onClick={() => setSelected(l)} className={`w-full text-left p-4 md:p-5 border-2 rounded-md transition-all ${selected?.id === l.id ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`loc-${l.id}`}>
                <div className="font-serif-display text-xl md:text-2xl text-slate-900">{l.name}</div>
                <div className="text-slate-600 text-xs md:text-sm mt-1">{l.city}, {l.country}</div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {l.services?.map((s) => <span key={s} className="text-[9px] md:text-[10px] uppercase tracking-widest border border-slate-300 px-2 py-0.5 text-slate-600 rounded">{s}</span>)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {selected && (
          <div className="mt-10 max-w-3xl">
            <div className="overline mb-2">{t.book.bookingAt}</div>
            <div className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-4">{selected.name}</div>
            <button onClick={() => setChatOpen(true)} className="btn-soft" data-testid="check-availability-btn">
              <Bot size={14} /> {t.book.checkAvail} →
            </button>
          </div>
        )}
      </section>

      {/* Sessions pricing — now BELOW Book Your Demo */}
      <section className="py-14 px-6 md:px-12 max-w-[1440px] mx-auto" data-testid="sessions-section">
        <div className="overline mb-3">{t.book.pricing}</div>
        <h2 className="font-serif-display text-3xl md:text-5xl text-slate-900 mb-10">{t.book.sessionsRentals}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {sessions.map((s) => (
            <button key={s.id} onClick={() => setChatOpen(true)} className="card-clean p-4 md:p-5 text-left hover:border-sky-500 transition-colors group" data-testid={`session-${s.id}`}>
              {s.kind === "rental" && <div className="overline !text-emerald-600 mb-1">🇧🇷 {t.book.rentalLabel}</div>}
              <div className="font-serif-display text-xl md:text-2xl text-slate-900 group-hover:text-sky-500 transition-colors">{s.label}</div>
              <div className="overline mt-1">{s.duration_min} min</div>
              <div className="font-serif-display text-2xl md:text-3xl text-sky-600 mt-3">{fmtSession(s)}</div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest font-ui text-slate-500 group-hover:text-sky-500 mt-3">{t.book.reserve} →</div>
            </button>
          ))}
        </div>
      </section>

      {chatOpen && (
        <AIBookingAgent
          onClose={() => setChatOpen(false)}
          sessions={sessions}
          fmt={fmtSession}
          locationId={selected?.id}
          t={t}
          locale={locale}
          country={country}
        />
      )}
    </main>
  );
}

function Perk({ icon, t, d }) {
  return (
    <div className="card-clean p-5 md:p-6" data-testid="perk-card">
      <div className="w-10 h-10 rounded-full bg-sky-50 text-sky-500 grid place-items-center mb-3">{icon}</div>
      <div className="font-serif-display text-lg md:text-2xl text-slate-900">{t}</div>
      <p className="text-slate-600 text-xs md:text-sm mt-2 leading-relaxed">{d}</p>
    </div>
  );
}

function AIBookingAgent({ onClose, sessions, fmt, locationId, t, locale, country }) {
  const initialIntro = t.book.chatIntro;
  const [messages, setMessages] = useState([{ role: "bot", text: initialIntro }]);
  const [picked, setPicked] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [showSessions, setShowSessions] = useState(false); // only after AI recommends or user explicitly asks
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const sessionIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, picked, done, thinking, recommended, showSessions, upsell]);

  const sendToAgent = async (text) => {
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setThinking(true);
    try {
      const res = await axios.post(`${API}/agent/booking/chat`, {
        session_id: sessionIdRef.current,
        message: text,
        locale,
        country: country || "",
        location_id: locationId || null,
        channel_origin: "website-book",
      }, websiteChannelHeaders("website-book"));
      setMessages((m) => [...m, { role: "bot", text: res.data.reply }]);
      if (res.data.recommended_session) {
        setRecommended(res.data.recommended_session);
        setShowSessions(true);
      }
      if (res.data.upsell) setUpsell(res.data.upsell);
      // Detect explicit "show prices" intent
      if (/(price|preço|preços|valor|cost|cuánto|cuanto|opções|options)/i.test(text)) {
        setShowSessions(true);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: t.book.connectionError }]);
    } finally {
      setThinking(false);
    }
  };

  const submitInput = (e) => {
    e?.preventDefault();
    if (!input.trim() || thinking) return;
    sendToAgent(input.trim());
  };

  const pickSession = (s) => {
    const lockMsg = t.book.lockMsg;
    setPicked(s);
    setMessages((m) => [...m, { role: "user", text: `${s.label} · ${fmt(s)}` }, { role: "bot", text: lockMsg }]);
  };

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lessons/reserve`, {
        session_id: picked.id,
        location_id: locationId || null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: form.date || null,
        notes: form.notes,
        locale,
      });
      setDone(res.data);
      setMessages((m) => [...m, { role: "bot", text: t.book.ok }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "bot", text: t.book.supportError }]);
    } finally {
      setSubmitting(false);
    }
  };

  const upsellLabel = upsell ? upsell[`label_${locale}`] || upsell.label_en : "";

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[80] grid place-items-center p-4" onClick={onClose} data-testid="ai-agent-modal">
      <div className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Sparkles size={18} /></div>
            <div>
              <div className="font-ui font-bold text-sm tracking-wider">{t.book.chatTitle}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-90">SharkX Sales · Booking</div>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded p-1" data-testid="ai-agent-close">✕</button>
        </div>
        <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-y-auto bg-slate-50" data-testid="agent-messages">
          {messages.map((m, i) => (
            <div key={i} className={`text-sm rounded-2xl px-4 py-2 max-w-[85%] whitespace-pre-line ${m.role === "bot" ? "bg-white border border-slate-200 text-slate-800" : "bg-sky-500 text-white ml-auto"}`}>
              {m.text}
            </div>
          ))}
          {thinking && (
            <div className="text-sm rounded-2xl px-4 py-2 max-w-[85%] bg-white border border-slate-200 text-slate-500" data-testid="agent-thinking">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}} />
                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}} />
              </span>
            </div>
          )}
          {recommended && !picked && !done && (
            <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border-2 border-sky-200 rounded-xl p-4" data-testid="agent-recommendation">
              <div className="overline mb-1">{t.book.sharkiRecommends}</div>
              <div className="font-serif-display text-2xl text-slate-900">{recommended.label}</div>
              <div className="font-serif-display text-3xl text-sky-600 mt-1">{fmt(recommended)}</div>
              <button onClick={() => pickSession(recommended)} className="btn-primary mt-3 w-full !justify-center" data-testid="agent-rec-pick">
                {t.book.reserve} →
              </button>
            </div>
          )}
          {showSessions && !picked && !done && (
            <div className="grid grid-cols-2 gap-2 mt-3" data-testid="agent-pricing-buttons">
              {sessions.map((s) => (
                <button key={s.id} onClick={() => pickSession(s)} className="text-left p-3 rounded-lg bg-white border-2 border-slate-200 hover:border-sky-500 transition-colors" data-testid={`agent-session-${s.id}`}>
                  <div className="font-semibold text-slate-900 text-sm">{s.label}</div>
                  <div className="text-sky-600 font-serif-display text-lg">{fmt(s)}</div>
                </button>
              ))}
            </div>
          )}
          {picked && !done && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 mt-2 space-y-2" data-testid="agent-form">
              <div className="overline">{t.book.reserve}: {picked.label} · {fmt(picked)}</div>
              <input required placeholder={t.book.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none" data-testid="agent-name" />
              <input required type="email" placeholder={t.book.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none" data-testid="agent-email" />
              <input required type="tel" placeholder={t.book.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none" data-testid="agent-phone" />
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-white border border-slate-300 px-3 py-2 rounded text-sm focus:border-sky-500 focus:outline-none" data-testid="agent-date" />
              <button disabled={submitting || !form.name || !form.email || !form.phone} onClick={submit} className="btn-primary w-full !justify-center !py-3 disabled:opacity-50" data-testid="agent-submit">
                {submitting ? t.book.cta : <><Check size={14} /> {t.book.reserve}</>}
              </button>
            </div>
          )}
          {done && upsell && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="agent-upsell">
              <div className="overline !text-amber-700 mb-1">★ {t.book.specialOffer}</div>
              <div className="font-serif-display text-xl md:text-2xl text-slate-900">{upsellLabel}</div>
              {upsell.id === "package3" && (
                <>
                  <div className="font-serif-display text-2xl text-amber-600 mt-2">{currency_fmt(upsell, locale)}</div>
                  <button onClick={() => alert(t.book.comingSoon)} className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded font-ui font-semibold uppercase tracking-wider text-xs" data-testid="agent-upsell-pkg">
                    {t.book.wantThisPack}
                  </button>
                </>
              )}
              {upsell.id === "gear" && (
                <a href={upsell.link || "/products/efoils"} className="mt-3 block text-center w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded font-ui font-semibold uppercase tracking-wider text-xs" data-testid="agent-upsell-gear">
                  {t.book.browseBoards} →
                </a>
              )}
            </div>
          )}
          {done && !upsell && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-4 text-sm" data-testid="agent-success">
              ✓ {t.book.ok}
            </div>
          )}
        </div>
        <form onSubmit={submitInput} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t.book.yourReply} className="flex-1 bg-transparent outline-none text-sm text-slate-900 px-2" data-testid="agent-input" disabled={thinking} />
          <button type="submit" disabled={thinking || !input.trim()} className="bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white rounded-full w-9 h-9 grid place-items-center" data-testid="agent-send"><Send size={14} /></button>
        </form>
        <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
          className="bg-emerald-500 hover:bg-emerald-600 text-white py-3 text-sm font-ui font-semibold uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="agent-whatsapp">
          <MessageCircle size={16} /> {t.contact.whatsapp}
        </a>
      </div>
    </div>
  );
}

function currency_fmt(upsell, locale) {
  const isBR = locale === "pt";
  const v = isBR ? upsell.price_brl : upsell.price_usd;
  if (isBR) return `R$ ${Number(v).toLocaleString("pt-BR")}`;
  return `$${Number(v).toLocaleString("en-US")}`;
}
